/**
 * Infraestrutura e contexto — distância da Marcação à rede elétrica e à
 * logística que pesa para quem vai instalar (datacenter, hidrogênio verde,
 * indústria), mais a Vocação principal do município.
 */

import * as turf from "@turf/turf";
import { ApiGetDistanceToLayer } from "../../../services/api";
import { geoServer, geoServerDefaultParams } from "../../../services/geoServer";
import { formatMaturidade } from "../../../utils/vocacoes";
import { VOCACAO_LAYER_NAME } from "../../../global/constants/categoricalLegends";
import type { InfrastructureDistance, Vocacao } from "./types";

interface InfrastructureLayer {
  id: string;
  label: string;
  group: InfrastructureDistance["group"];
  layerName: string;
  cqlFilter?: string;
}

/** Rede elétrica muda com o modo do sistema, como no restante do Atlas. */
function electricLayers(systemMode: "sin" | "distribuidora"): InfrastructureLayer[] {
  return systemMode === "sin"
    ? [
        {
          id: "linha_transmissao",
          label: "Linha de transmissão (SIN)",
          group: "energia",
          layerName: "linhas_de_transmissao_base_existente",
        },
        {
          id: "subestacao",
          label: "Subestação (SIN)",
          group: "energia",
          layerName: "subestacoes_base_existente",
          cqlFilter: "nome NOT LIKE '%PISF%'",
        },
      ]
    : [
        {
          id: "linha_transmissao",
          label: "Linha de alta tensão (Neoenergia)",
          group: "energia",
          layerName: "LTS-neoenergia",
        },
        {
          id: "subestacao",
          label: "Subestação (Neoenergia)",
          group: "energia",
          layerName: "neoenergia_pe_untrat",
        },
      ];
}

const OTHER_LAYERS: InfrastructureLayer[] = [
  {
    id: "gasoduto",
    label: "Gasoduto de transporte",
    group: "energia",
    layerName: "gasoduto_transporte_pe",
  },
  {
    id: "rodovia_federal",
    label: "Rodovia federal",
    group: "logistica",
    layerName: "rodovias_federais",
  },
  {
    id: "rodovia_estadual",
    label: "Rodovia estadual",
    group: "logistica",
    layerName: "rodovias_estaduais",
  },
  { id: "porto", label: "Porto", group: "logistica", layerName: "portos" },
  { id: "adutora", label: "Adutora", group: "logistica", layerName: "adutoras" },
];

export function infrastructureLayers(
  systemMode: "sin" | "distribuidora"
): InfrastructureLayer[] {
  return [...electricLayers(systemMode), ...OTHER_LAYERS];
}

const NAME_FIELDS = ["nome", "nomelongo", "name", "nm_rodovia", "rodovia", "codigo"];

function featureName(properties: Record<string, unknown> | undefined): string | null {
  if (!properties) return null;
  for (const field of NAME_FIELDS) {
    const value = properties[field];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

/**
 * O endpoint de distância só aceita Point ou Polygon. Um município pode vir
 * como MultiPolygon; nesse caso a distância é medida a partir do maior
 * polígono, que é onde está quase toda a área.
 */
export function toDistanceGeometry(
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
): GeoJSON.Polygon {
  if (geometry.type === "Polygon") return geometry;
  let largest: GeoJSON.Polygon | null = null;
  let largestArea = -1;
  for (const coordinates of geometry.coordinates) {
    const polygon: GeoJSON.Polygon = { type: "Polygon", coordinates };
    const area = turf.area(polygon);
    if (area > largestArea) {
      largest = polygon;
      largestArea = area;
    }
  }
  return largest ?? { type: "Polygon", coordinates: geometry.coordinates[0] };
}

/** Distâncias a toda a infraestrutura. Uma camada que falha vira `null`. */
export async function fetchInfrastructure(
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
  systemMode: "sin" | "distribuidora"
): Promise<InfrastructureDistance[]> {
  const distanceGeometry = toDistanceGeometry(geometry);
  const layers = infrastructureLayers(systemMode);
  const results = await Promise.allSettled(
    layers.map((layer) =>
      ApiGetDistanceToLayer({
        geometry: distanceGeometry,
        layer_name: layer.layerName,
        cql_filter: layer.cqlFilter,
      })
    )
  );
  return layers.map((layer, i) => {
    const result = results[i];
    const nearest =
      result.status === "fulfilled" ? result.value.nearest_feature : null;
    return {
      id: layer.id,
      label: layer.label,
      group: layer.group,
      distanceKm: nearest ? nearest.distance_km : null,
      featureName: nearest ? featureName(nearest.properties) : null,
    };
  });
}

/** Vocação principal (ADEPE) do município, ou `null` se não houver. */
export async function fetchVocacaoPrincipal(
  municipalityName: string
): Promise<Vocacao | null> {
  try {
    const escaped = municipalityName.replace(/'/g, "''");
    const response = await geoServer.get("wfs", {
      params: {
        ...geoServerDefaultParams,
        typename: `pepe-atlas:${VOCACAO_LAYER_NAME}`,
        cql_filter: `nm_mun='${escaped}'`,
        propertyName: "nm_mun,vocacao_1,classificacao_1",
        maxFeatures: 1,
      },
    });
    const properties = response.data?.features?.[0]?.properties;
    const nome = typeof properties?.vocacao_1 === "string" ? properties.vocacao_1.trim() : "";
    if (!nome) return null;
    return { nome, maturidade: formatMaturidade(properties.classificacao_1) };
  } catch {
    return null;
  }
}
